import React from 'react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useProgressiveText } from '../../hooks/useProgressiveText';
import { VisualizerPlaceholder } from '../visualization/VisualizerPlaceholder';

interface StreamingContentProps {
  content: string;
  isStreaming: boolean;
  className?: string;
}

/**
 * StreamingContent - Progressive character reveal with full markdown rendering.
 *
 * During streaming:
 * - Characters appear one by one via useProgressiveText (typewriter effect)
 * - MarkdownRenderer processes the full displayed text on every frame
 * - Incomplete XML tags are stripped via isStreaming mode
 *
 * Visualizer fences: when a ```visualizer fence is open but not yet closed,
 * content before the fence is rendered as markdown and a placeholder is shown.
 */

/**
 * Check if content has an unclosed ```visualizer fence.
 * Returns the index where the fence opens, or -1 if no unclosed fence.
 */
function findUnclosedVisualizerFence(content: string): number {
  const openPattern = '```visualizer\n';
  let searchFrom = 0;

  while (true) {
    const openIdx = content.indexOf(openPattern, searchFrom);
    if (openIdx === -1) return -1;

    const afterOpen = openIdx + openPattern.length;
    const closeIdx = content.indexOf('```', afterOpen);
    if (closeIdx === -1) return openIdx;

    searchFrom = closeIdx + 3;
  }
}
export const StreamingContent: React.FC<StreamingContentProps> = React.memo(({
  content,
  isStreaming,
  className,
}) => {
  const displayedText = useProgressiveText(content, isStreaming);

  // trimEnd() au RENDU : les modèles émettent souvent des sauts de ligne en fin de
  // message juste avant d'appeler un tool / une carte JP. Le reducer ne les nettoie
  // qu'à la FERMETURE du segment (closeActiveContentSegment), or il existe une fenêtre
  // où le texte + ses \n finaux sont déjà affichés mais l'item non-texte suivant n'est
  // pas encore arrivé (typiquement : une recherche JP lancée, mais sa box pas encore
  // rendue, l'agent en pause le temps que le backend démarre). Pendant cette fenêtre le
  // segment reste actif → sans ce trim, MarkdownRenderer rend les \n finaux en
  // paragraphes vides = un grand trou fantôme entre le texte et la plume.
  // Inoffensif pour le typewriter : un \n final redevient interne (donc conservé) dès
  // qu'un caractère le suit ; et sans effet quand le segment est déjà fermé (déjà trimmé).
  const visibleText = displayedText.trimEnd();

  if (visibleText.length === 0) return null;

  if (!isStreaming) {
    return <MarkdownRenderer content={visibleText} className={className} />;
  }

  // Buffer unclosed visualizer fences — show placeholder while streaming
  const unclosedIdx = findUnclosedVisualizerFence(visibleText);
  if (unclosedIdx >= 0) {
    const beforeFence = visibleText.slice(0, unclosedIdx);
    return (
      <>
        {beforeFence && <MarkdownRenderer content={beforeFence} className={className} isStreaming />}
        <VisualizerPlaceholder />
      </>
    );
  }

  return <MarkdownRenderer content={visibleText} className={className} isStreaming />;
});

StreamingContent.displayName = 'StreamingContent';
